
import React, { useState, useEffect } from 'react';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import {
  FileText,
  Calendar,
  Building2,
  Check,
  X,
  Clock,
  CreditCard,
  Info
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PEOQuote } from '@/types/quotes.types';
import { LinkedUser } from '@/types/broker.types';
import { useBrokerQuotes } from '@/hooks/broker/use-broker-quotes';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface LinkedUsersQuotesProps {
  linkedUsers: LinkedUser[];
  isLoading: boolean;
}

export const LinkedUsersQuotes = ({ linkedUsers, isLoading }: LinkedUsersQuotesProps) => {
  // This component is being replaced by BrokerQuoteTools for enhanced functionality
  // Redirect to the new component
  const BrokerQuoteTools = React.lazy(() => import('./BrokerQuoteTools').then(module => ({ default: module.BrokerQuoteTools })));
  
  return (
    <React.Suspense fallback={<QuotesLoadingState />}>
      <BrokerQuoteTools linkedUsers={linkedUsers} isLoading={isLoading} />
    </React.Suspense>
  );
};

const QuotesLoadingState = () => (
  <div className="space-y-4">
    {[1, 2].map((i) => (
      <Card key={i}>
        <CardHeader>
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <div className="flex justify-end">
              <Skeleton className="h-9 w-32" />
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

const QuotesEmptyState = () => (
  <Card>
    <CardHeader>
      <CardTitle>Client Quotes</CardTitle>
      <CardDescription>No quotes available yet</CardDescription>
    </CardHeader>
    <CardContent className="text-center py-6">
      <div className="flex flex-col items-center gap-4">
        <div className="rounded-full bg-muted p-3">
          <Info className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-muted-foreground">
          When your linked clients receive quotes from PEO providers, they will appear here.
        </p>
      </div>
    </CardContent>
  </Card>
);
